
import { useCallback, useMemo, useRef, useState } from 'react';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import { fetchStandardRatios } from '../../../services/analysisRatiosStandard';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { exportStandardRatiosPdf } from './standardRatiosPdfExport';
import './StandardRatiosPage.css';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const UI_TEXT = {
  'tr-TR': {
    title: 'Standard Intelligence',
    description: 'CEO ve CFO icin sirketin standartlara uyum, sapma ve risk gorunumu.',
    calculating: 'Hesaplaniyor...',
    exportingPdf: 'PDF olusturuluyor...',
    exportPdf: 'PDF Al',
    calculate: 'Hesapla',
    reportingBasis: 'Raporlama Bazi',
    ifrsDefault: 'IFRS (Varsayilan)',
    vukOption: 'VUK',
    activeBasis: 'Analiz Bazi',
    selectCompanyPeriod: 'Lutfen sirket ve donem secin.',
    ratioCalcError: 'Rasyolar hesaplanamadi.',
    strong: 'Guclu',
    balanced: 'Dengeli',
    risky: 'Riskli',
    noData: 'Veri Yok',
    lowRisk: 'Dusuk Risk',
    caution: 'Dikkat',
    alarm: 'Alarm',
    overallComplianceScore: 'Genel Uyum Skoru',
    aboveStandardDensity: 'Standart Ustu Yogunluk',
    leverageAlarm: 'Kaldirac Alarmi',
    company: 'Sirket',
    standard: 'Std',
    aiSnapshot: 'AI Snapshot',
    score: 'Skor',
    criticalDeviation: 'Kritik Sapma',
    dataTrust: 'Data Trust',
    criticalDeviations: 'Kritik Sapmalar',
    top5: 'Top 5',
    noDeviationData: 'Sapma verisi yok.',
    companyVsStandardBullet: 'Sirket / Standart Bullet Karsilastirmasi',
    sortedByDeviation: 'Sapmaya gore sirali',
    noCompareData: 'Karsilastirma verisi yok.',
    diff: 'Fark',
    strengthVsWeakness: 'Strength vs Weakness',
    executiveSummary: 'Ust yonetim ozeti',
    strengths: 'Guclu Yonler',
    weaknesses: 'Zayif Yonler',
    executiveTable: 'Yonetici Tablosu',
    ratioCount: 'rasyo',
    ratio: 'Rasyo',
    formula: 'Formula',
    calculated: 'Hesaplanan',
    deviation: 'Sapma',
    status: 'Durum',
    note: 'Not',
    noRatioData: 'Gosterilecek rasyo yok. Hesapla butonunu kullanin.',
    dataQualityIndicator: 'Data Quality Indicator',
    trustScore: 'Guven Skoru',
    noDataCount: 'Veri Yok',
    missingData: 'Eksik Veri',
    suspiciousData: 'Supheli Veri',
    coverage: 'coverage'
  },
  'en-US': {
    title: 'Standard Intelligence',
    description: 'Compliance, deviation and risk view for CEOs and CFOs.',
    calculating: 'Calculating...',
    exportingPdf: 'Building PDF...',
    exportPdf: 'Export PDF',
    calculate: 'Calculate',
    reportingBasis: 'Reporting Basis',
    ifrsDefault: 'IFRS (Default)',
    vukOption: 'VUK',
    activeBasis: 'Analysis Basis',
    selectCompanyPeriod: 'Please select company and period.',
    ratioCalcError: 'Ratios could not be calculated.',
    strong: 'Strong',
    balanced: 'Balanced',
    risky: 'Risky',
    noData: 'No Data',
    lowRisk: 'Low Risk',
    caution: 'Caution',
    alarm: 'Alarm',
    overallComplianceScore: 'Overall Compliance Score',
    aboveStandardDensity: 'Above Standard Density',
    leverageAlarm: 'Leverage Alarm',
    company: 'Company',
    standard: 'Std',
    aiSnapshot: 'AI Snapshot',
    score: 'Score',
    criticalDeviation: 'Critical Deviation',
    dataTrust: 'Data Trust',
    criticalDeviations: 'Critical Deviations',
    top5: 'Top 5',
    noDeviationData: 'No deviation data.',
    companyVsStandardBullet: 'Company / Standard Bullet Comparison',
    sortedByDeviation: 'Sorted by deviation',
    noCompareData: 'No comparison data.',
    diff: 'Diff',
    strengthVsWeakness: 'Strength vs Weakness',
    executiveSummary: 'Executive summary',
    strengths: 'Strengths',
    weaknesses: 'Weaknesses',
    executiveTable: 'Executive Table',
    ratioCount: 'ratios',
    ratio: 'Ratio',
    formula: 'Formula',
    calculated: 'Calculated',
    deviation: 'Deviation',
    status: 'Status',
    note: 'Note',
    noRatioData: 'No ratios to display. Use the Calculate button.',
    dataQualityIndicator: 'Data Quality Indicator',
    trustScore: 'Trust Score',
    noDataCount: 'No Data',
    missingData: 'Missing Data',
    suspiciousData: 'Suspicious Data',
    coverage: 'coverage'
  }
};

const RATIO_TITLE_TRANSLATIONS = {
  debt_to_equity_ratio: 'Debt to Equity Ratio',
  return_on_equity: 'Return on Equity (ROE)',
  return_on_assets: 'Return on Assets (ROA)',
  net_profit_margin: 'Net Profit Margin'
};

const humanizeRatioKey = (key) => (key || '')
  .split('_')
  .filter(Boolean)
  .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
  .join(' ');

const formatValue = (value, unit, locale = 'tr-TR') => {
  if (value === null || value === undefined) {
    return '-';
  }
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  if (unit === '%') {
    return `${(number * 100).toLocaleString(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}%`;
  }
  return number.toLocaleString(locale, { maximumFractionDigits: 4, minimumFractionDigits: 2 });
};

const formatSignedPercent = (value, locale = 'tr-TR') => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return `${number > 0 ? '+' : ''}${number.toLocaleString(locale, { maximumFractionDigits: 1, minimumFractionDigits: 1 })}%`;
};

const deviationPercent = (value, standard) => {
  const v = Number(value);
  const s = Number(standard);
  if (Number.isNaN(v) || Number.isNaN(s) || Math.abs(s) < 0.000001) {
    return null;
  }
  return ((v - s) / Math.abs(s)) * 100;
};

const verdictFromScore = (score) => {
  if (score >= 75) {
    return 'Guclu';
  }
  if (score >= 50) {
    return 'Dengeli';
  }
  return 'Riskli';
};

function StandardRatiosPage() {
  const { language } = useLanguage();
  const t = useCallback((key) => UI_TEXT[language]?.[key] || UI_TEXT['tr-TR'][key] || key, [language]);
  const translateRatioTitle = (row) => {
    if (!row) {
      return '';
    }
    if (!language.startsWith('en')) {
      return row.title;
    }
    return RATIO_TITLE_TRANSLATIONS[row.key] || humanizeRatioKey(row.key) || row.title;
  };
  const [selection, setSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [data, setData] = useState(null);
  const [basis, setBasis] = useState('IFRS');
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [message, setMessage] = useState('');
  const pageCaptureRef = useRef(null);

  const hasSelection = Boolean(selection.companyId && selection.periodId);
  const rows = useMemo(() => data?.items || [], [data]);

  const summary = useMemo(
    () => ({
      aboveCount: data?.aboveCount || 0,
      belowCount: data?.belowCount || 0,
      equalCount: data?.equalCount || 0,
      noDataCount: data?.noDataCount || 0
    }),
    [data]
  );

  const handleCalculate = async () => {
    if (!hasSelection) {
      setMessage(t('selectCompanyPeriod'));
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const response = await fetchStandardRatios(selection.companyId, selection.periodId, basis);
      setData(response);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('ratioCalcError'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const preparedRows = useMemo(() => {
    return rows.map((row) => {
      const calculated = Number(row.calculatedValue || 0);
      const standard = Number(row.standardValue || 0);
      const deviation = deviationPercent(calculated, standard) || 0;
      const domain = Math.max(calculated, standard, 0.0001) * 1.2;
      const comparison = String(row.comparison || '').toLowerCase();
      const issueType = comparison === 'no_data'
        ? 'data_quality'
        : comparison === 'below'
          ? (row.key === 'debt_to_equity_ratio' || row.key === 'debt_ratio' ? 'risk' : 'performance')
          : comparison === 'equal'
            ? 'neutral'
            : 'positive';

      return {
        ...row,
        calculated,
        standard,
        deviation,
        cappedDeviation: clamp(deviation, -250, 250),
        domain,
        issueType
      };
    });
  }, [rows]);

  const score = useMemo(() => {
    const positives = preparedRows.filter((row) => row.issueType === 'positive').length;
    const critical = preparedRows.filter((row) => (row.issueType === 'risk' || row.issueType === 'performance') && Math.abs(row.deviation) >= 25).length;
    const missing = preparedRows.filter((row) => row.issueType === 'data_quality').length;
    return clamp((positives * 7) - (critical * 12) - (missing * 6) + 42, 0, 100);
  }, [preparedRows]);

  const verdictKey = verdictFromScore(score);
  const verdict = verdictKey === 'Guclu' ? t('strong') : verdictKey === 'Dengeli' ? t('balanced') : t('risky');
  const coverage = preparedRows.length ? clamp(((preparedRows.length - summary.noDataCount) / preparedRows.length) * 100, 0, 100) : 0;

  const leverageRow = preparedRows.find((row) => row.key === 'debt_to_equity_ratio');
  const leverageRisk = !leverageRow
    ? t('noData')
    : leverageRow.calculated <= leverageRow.standard
      ? t('lowRisk')
      : leverageRow.calculated <= leverageRow.standard * 1.35
        ? t('caution')
        : t('alarm');

  const criticalRows = useMemo(
    () => [...preparedRows].sort((left, right) => Math.abs(right.deviation) - Math.abs(left.deviation)).slice(0, 5),
    [preparedRows]
  );

  const strengthRows = preparedRows.filter((row) => row.issueType === 'positive').slice(0, 4);
  const weaknessRows = preparedRows.filter((row) => row.issueType === 'risk' || row.issueType === 'performance').slice(0, 4);

  const missingCount = preparedRows.filter((row) => row.issueType === 'data_quality').length;
  const suspiciousCount = preparedRows.filter((row) => Math.abs(row.deviation) > 300).length;
  const trustScore = clamp(100 - (missingCount * 10) - (suspiciousCount * 6), 0, 100);

  const handleExportPdf = useCallback(async () => {
    if (!pageCaptureRef.current || exportingPdf) {
      return;
    }
    try {
      setExportingPdf(true);
      await exportStandardRatiosPdf({
        element: pageCaptureRef.current,
        companyName: selection.company?.name || selection.company?.title || '',
        periodLabel: selection.period?.label || selection.period?.description || selection.period?.code || '',
        language
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('ratioCalcError'));
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, language, selection.company, selection.period, t]);

  return (
    <div className="card standard-ratios-card" ref={pageCaptureRef}>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          <div className="standard-ratios-page-actions" data-export-hidden="true">
            <button type="button" className="btn-secondary" disabled={!hasSelection || loading || exportingPdf} onClick={handleExportPdf}>
              {exportingPdf ? t('exportingPdf') : t('exportPdf')}
            </button>
            <button type="button" className="btn-primary" disabled={!hasSelection || loading || exportingPdf} onClick={handleCalculate}>
              {loading ? t('calculating') : t('calculate')}
            </button>
          </div>
        }
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} locale={language} />

      <section className="sector-ratio-analysis-controls" data-export-hidden="true">
        <label htmlFor="standard-ratio-basis-select">
          {t('reportingBasis')}
          <select id="standard-ratio-basis-select" value={basis} onChange={(event) => setBasis(event.target.value)}>
            <option value="IFRS">{t('ifrsDefault')}</option>
            <option value="VUK">{t('vukOption')}</option>
          </select>
        </label>
      </section>

      {message && <p className="standard-ratios-message">{message}</p>}
      {data?.reportingBasis && <p className="standard-ratios-message">{t('activeBasis')}: {data.reportingBasis}</p>}

      <section className="sr-executive-grid">
        <article className="sr-exec-card sr-gauge-card">
          <header><span>{t('overallComplianceScore')}</span><strong>{formatValue(score, null, language)}</strong></header>
          <div className="sr-gauge" aria-hidden="true"><div className="sr-gauge-center">{formatValue(score, null, language)}</div></div>
          <footer><span className={`sr-verdict sr-verdict-${verdict.toLowerCase()}`}>{verdict}</span></footer>
        </article>

        <article className="sr-exec-card">
          <header><span>{t('aboveStandardDensity')}</span><strong>{summary.aboveCount}/{preparedRows.length || 0}</strong></header>
          <div className="sr-progress"><span style={{ width: `${preparedRows.length ? (summary.aboveCount / preparedRows.length) * 100 : 0}%` }} /></div>
          <footer><span>{formatValue(coverage, null, language)}% {t('coverage')}</span></footer>
        </article>

        <article className="sr-exec-card">
          <header><span>{t('leverageAlarm')}</span><strong>{leverageRisk}</strong></header>
          <div className="sr-thermometer"><span style={{ height: `${!leverageRow || leverageRow.standard <= 0 ? 0 : clamp((leverageRow.calculated / (leverageRow.standard * 2)) * 100, 0, 100)}%` }} /></div>
          <footer><span>{t('company')}: {formatValue(leverageRow?.calculatedValue, leverageRow?.unit, language)} | {t('standard')} {formatValue(leverageRow?.standardValue, leverageRow?.unit, language)}</span></footer>
        </article>

        <article className="sr-exec-card">
          <header><span>{t('aiSnapshot')}</span><strong>{verdict}</strong></header>
          <div className="sr-mini-snapshot">
            <div><small>{t('score')}</small><b>{formatValue(score, null, language)}</b></div>
            <div><small>{t('criticalDeviation')}</small><b>{translateRatioTitle(criticalRows[0]) || '-'}</b></div>
            <div><small>{t('dataTrust')}</small><b>{formatValue(trustScore, null, language)}%</b></div>
          </div>
        </article>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('criticalDeviations')}</h3><span>{t('top5')}</span></div>
        <div className="sr-diverging-list">
          {!criticalRows.length ? <p className="sr-empty">{t('noDeviationData')}</p> : criticalRows.map((row) => (
            <article key={row.key} className="sr-diverging-row">
              <div className="sr-diverging-head"><strong>{translateRatioTitle(row)}</strong><span>{formatSignedPercent(row.deviation, language)}</span></div>
              <div className="sr-diverging-track"><span className="sr-diverging-center" /><span className={`sr-diverging-bar ${row.cappedDeviation >= 0 ? 'positive' : 'negative'}`} style={{ width: `${Math.min(50, Math.abs(row.cappedDeviation) / 5)}%`, [row.cappedDeviation >= 0 ? 'left' : 'right']: '50%' }} /></div>
            </article>
          ))}
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('companyVsStandardBullet')}</h3><span>{t('sortedByDeviation')}</span></div>
        <div className="sr-bullet-list">
          {!preparedRows.length ? <p className="sr-empty">{t('noCompareData')}</p> : [...preparedRows].sort((left, right) => Math.abs(right.deviation) - Math.abs(left.deviation)).map((row) => (
            <article key={row.key} className="sr-bullet-row">
              <div className="sr-bullet-head"><strong>{translateRatioTitle(row)}</strong><span>{t('company')} {formatValue(row.calculatedValue, row.unit, language)} | {t('standard')} {formatValue(row.standardValue, row.unit, language)} | {t('diff')} {formatSignedPercent(row.deviation, language)}</span></div>
              <div className="sr-bullet-track"><span className="sr-bullet-zones" /><span className="sr-bullet-company" style={{ width: `${clamp((row.calculated / row.domain) * 100, 0, 100)}%` }} /><span className="sr-bullet-standard" style={{ left: `${clamp((row.standard / row.domain) * 100, 0, 100)}%` }} /></div>
            </article>
          ))}
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('strengthVsWeakness')}</h3><span>{t('executiveSummary')}</span></div>
        <div className="sr-sw-grid">
          <article>
            <h4>{t('strengths')}</h4>
            {strengthRows.length ? strengthRows.map((row) => <p key={row.key}>{translateRatioTitle(row)} <b>{formatSignedPercent(row.deviation, language)}</b></p>) : <p className="sr-empty">-</p>}
          </article>
          <article>
            <h4>{t('weaknesses')}</h4>
            {weaknessRows.length ? weaknessRows.map((row) => <p key={row.key}>{translateRatioTitle(row)} <b>{formatSignedPercent(row.deviation, language)}</b></p>) : <p className="sr-empty">-</p>}
          </article>
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('executiveTable')}</h3><span>{preparedRows.length} {t('ratioCount')}</span></div>
        <div className="standard-ratios-table-wrapper">
          <table className="standard-ratios-table">
            <thead>
              <tr>
                <th>{t('ratio')}</th>
                <th>{t('formula')}</th>
                <th>{t('calculated')}</th>
                <th>{t('standard')}</th>
                <th>{t('deviation')}</th>
                <th>{t('status')}</th>
                <th>{t('note')}</th>
              </tr>
            </thead>
            <tbody>
              {preparedRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="standard-ratios-empty">{loading ? t('calculating') : t('noRatioData')}</td>
                </tr>
              ) : (
                [...preparedRows].sort((left, right) => Math.abs(right.deviation) - Math.abs(left.deviation)).map((row) => (
                  <tr key={row.key}>
                    <td><strong>{translateRatioTitle(row)}</strong><div className="standard-ratios-key">{row.key}</div></td>
                    <td className="standard-ratios-formula">{row.formula}</td>
                    <td>{formatValue(row.calculatedValue, row.unit, language)}</td>
                    <td>{formatValue(row.standardValue, row.unit, language)}</td>
                    <td>{formatSignedPercent(row.deviation, language)}</td>
                    <td>
                      <span className={`standard-ratios-status standard-ratios-status-${String(row.comparison || '').toLowerCase()}`}>
                        {row.comparison}
                      </span>
                    </td>
                    <td>{row.note || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sr-section">
        <div className="sr-section-head"><h3>{t('dataQualityIndicator')}</h3><span>{t('trustScore')} {formatValue(trustScore, null, language)}%</span></div>
        <div className="sr-data-grid">
          <article><span>{t('noDataCount')}</span><strong>{summary.noDataCount}</strong></article>
          <article><span>{t('missingData')}</span><strong>{missingCount}</strong></article>
          <article><span>{t('suspiciousData')}</span><strong>{suspiciousCount}</strong></article>
          <article><span>{t('trustScore')}</span><strong>{formatValue(trustScore, null, language)}%</strong></article>
        </div>
      </section>
    </div>
  );
}

export default StandardRatiosPage;
